import React from "react";
import Chapter from "@/components/landing/Chapter";
import axios from "axios";
import { toast } from "sonner";
import Reveal from "@/components/landing/Reveal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const InfoForm = () => {
  const [form, setForm] = React.useState({
    nombre: "",
    apellidos: "",
    email: "",
    mensaje: "",
  });
  const [loading, setLoading] = React.useState(false);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((s) => ({ ...s, [name]: value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.nombre.trim() || !form.apellidos.trim() || !form.email.trim()) {
      toast.error("Por favor, completa nombre, apellidos y correo electrónico.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(
        `${API}/info-request`,
        {
          nombre: form.nombre.trim(),
          apellidos: form.apellidos.trim(),
          email: form.email.trim(),
          mensaje: form.mensaje.trim() || null,
        },
        { timeout: 10000 }
      );
      toast.success("Solicitud enviada. Te escribiremos muy pronto.");
      setForm({ nombre: "", apellidos: "", email: "", mensaje: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "No se ha podido enviar la solicitud. Inténtalo de nuevo."
      );
    } finally {
      setLoading(false);
    }
  };

  const fieldClass =
    "w-full bg-transparent border-b border-[#3F403A]/25 focus:border-[#63846F] focus:outline-none py-3 px-1 font-sans text-base text-[#3F403A] placeholder:text-[#3F403A]/40 transition-colors";

  return (
    <section
      id="info"
      data-testid="info-form-section"
      className="bg-[#FAF8F3]"
    >
      <div className="max-w-6xl mx-auto px-6 md:px-10 py-24 md:py-32">
        <div className="grid grid-cols-12 gap-10 md:gap-14 items-start">
          <div className="col-span-12 md:col-span-5">
            <Reveal>
              <Chapter number="12" label="Contacto" />
              <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] text-[#3F403A]">
                Solicita más
                <span className="italic text-[#63846F]"> información.</span>
              </h2>
              <p className="mt-8 font-sans text-base md:text-lg leading-relaxed text-[#3F403A]/80 max-w-md">
                Déjanos tus datos y nos pondremos en contacto contigo para
                resolver cualquier duda sobre la edición Primer Aniversario.
              </p>
            </Reveal>
          </div>

          <div className="col-span-12 md:col-span-7">
            <Reveal delay={140}>
              <form
                onSubmit={onSubmit}
                data-testid="info-form"
                className="border border-[#3F403A]/12 bg-[#F4EFE7] p-6 md:p-10 lg:p-12"
                noValidate
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                  <label className="block">
                    <span className="overline text-[#3F403A]/60 block mb-2">
                      Nombre
                    </span>
                    <input
                      type="text"
                      name="nombre"
                      value={form.nombre}
                      onChange={onChange}
                      required
                      autoComplete="given-name"
                      placeholder="Tu nombre"
                      className={fieldClass}
                      data-testid="info-form-nombre"
                    />
                  </label>
                  <label className="block">
                    <span className="overline text-[#3F403A]/60 block mb-2">
                      Apellidos
                    </span>
                    <input
                      type="text"
                      name="apellidos"
                      value={form.apellidos}
                      onChange={onChange}
                      required
                      autoComplete="family-name"
                      placeholder="Tus apellidos"
                      className={fieldClass}
                      data-testid="info-form-apellidos"
                    />
                  </label>
                  <label className="block md:col-span-2">
                    <span className="overline text-[#3F403A]/60 block mb-2">
                      Correo electrónico
                    </span>
                    <input
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={onChange}
                      required
                      autoComplete="email"
                      placeholder="nombre@empresa.com"
                      className={fieldClass}
                      data-testid="info-form-email"
                    />
                  </label>
                  <label className="block md:col-span-2">
                    <span className="overline text-[#3F403A]/60 block mb-2">
                      Mensaje (opcional)
                    </span>
                    <textarea
                      name="mensaje"
                      value={form.mensaje}
                      onChange={onChange}
                      rows={3}
                      placeholder="Cuéntanos qué te gustaría saber…"
                      className={`${fieldClass} resize-none`}
                      data-testid="info-form-mensaje"
                    />
                  </label>
                </div>

                <div className="mt-10 flex flex-col sm:flex-row sm:items-center gap-6 sm:justify-between">
                  <p className="font-sans text-xs text-[#3F403A]/60 max-w-sm leading-relaxed">
                    Al enviar, aceptas ser contactada por el equipo de Lidera
                    con Voz Propia respecto a esta edición.
                  </p>
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="info-form-submit"
                    className="inline-flex items-center justify-center gap-3 rounded-none border border-[#63846F] bg-[#63846F] text-[#FAF8F3] hover:bg-[#4E6D5A] hover:border-[#4E6D5A] transition-colors px-8 py-4 uppercase tracking-[0.22em] text-xs md:text-[13px] font-medium disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {loading ? "Enviando…" : "Enviar solicitud"}
                    {!loading && <span aria-hidden>→</span>}
                  </button>
                </div>
              </form>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};

export default InfoForm;
